// REACT
import React, { useMemo } from "react";

// MATERIAL UI
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

// FORMIK
import { Form, Formik } from "formik";
import * as Yup from "yup";

// I18N
import { defaultCountries, parseCountry } from "react-international-phone";

// FORM HELPERS
import { FormikText } from "src/components/forms/FormikText";

// COMPONENTS
import NotabeneHostedMount from "src/components/wallets/dropdown/NotabeneHostedMount";

// API
import {
  useSubmitDepositSenderInfoMutation,
} from "src/store/features/api/notabene";

interface DepositActionRequiredFormProps {
  /** Notabene transfer id (D11 quarantined inbound). */
  txId: string;
  /** Asset code, used by the SafeConnect deposit-assist mount. */
  asset: string;
  /** Source address (where the funds came from). */
  sourceAddress: string;
  /** Decimal amount as observed by Belfast. */
  amount?: number;
  /** Caller fires this when info collection is complete + status refreshes. */
  onComplete?: () => void;
}

interface FormValues {
  senderFullName: string;
  senderCountryIso: string;
  selfOrThirdParty: "self" | "third_party";
  // Third-party sender's address — Notabene's AU policy requires a full
  // originator address for an external sender (the self branch uses the
  // customer's KYC record server-side, so these stay hidden + optional).
  senderStreetAddress: string;
  senderTownName: string;
  senderPostCode: string;
}

const validationSchema = Yup.object({
  senderFullName: Yup.string().trim().min(2, "Please enter the sender's full name").required(),
  senderCountryIso: Yup.string()
    .trim()
    .length(2, "Choose the sender's country")
    .required(),
  selfOrThirdParty: Yup.string()
    .oneOf(["self", "third_party"])
    .required(),
  senderStreetAddress: Yup.string()
    .trim()
    .when("selfOrThirdParty", {
      is: "third_party",
      then: (schema) => schema.min(3, "Enter the sender's street address").required(),
      otherwise: (schema) => schema.notRequired(),
    }),
  senderTownName: Yup.string()
    .trim()
    .when("selfOrThirdParty", {
      is: "third_party",
      then: (schema) => schema.min(2, "Enter the sender's town or city").required(),
      otherwise: (schema) => schema.notRequired(),
    }),
  senderPostCode: Yup.string()
    .trim()
    .when("selfOrThirdParty", {
      is: "third_party",
      then: (schema) => schema.min(2, "Enter the sender's postcode").required(),
      otherwise: (schema) => schema.notRequired(),
    }),
});

interface CountryOption {
  iso: string;
  name: string;
}

/**
 * D13 — Customer-side info collection for an `ACTION_REQUIRED` inbound deposit.
 *
 * Flow:
 *   1. Customer fills sender name + country + chooses self vs third-party.
 *   2. **Self branch** — after submitting the form, mount the SafeConnect
 *      `deposit-assist` widget. The widget collects ownership attestation +
 *      counterparty-assist sharing if needed; on completion the backend
 *      reconciles the Notabene transfer.
 *   3. **Third-party branch** — submit the form payload directly to
 *      `POST /transfers/:id/sender-info`. Backend forwards to Notabene `txUpdate`.
 *
 * The component owns the staging between form submit and SafeConnect mount.
 * On `onComplete` the parent re-fetches the transfer status (which
 * transitions out of ACTION_REQUIRED once Notabene confirms).
 */
const DepositActionRequiredForm: React.FC<DepositActionRequiredFormProps> = ({
  txId,
  asset,
  sourceAddress,
  amount,
  onComplete,
}) => {
  const [submitSenderInfo, { isLoading: isSubmitting, error: submitError }] =
    useSubmitDepositSenderInfoMutation();
  const [stage, setStage] = React.useState<"form" | "safe-connect" | "submitted">("form");
  const [submittedValues, setSubmittedValues] = React.useState<FormValues | null>(null);

  const countries: CountryOption[] = useMemo(() => {
    return defaultCountries
      .map((c) => parseCountry(c))
      .map((c) => ({ iso: c.iso2.toUpperCase(), name: c.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const handleSubmit = async (values: FormValues) => {
    setSubmittedValues(values);
    if (values.selfOrThirdParty === "self") {
      // Stage the SafeConnect mount; we hold off on the API call until the
      // attestation completes so the backend gets the walletVerificationReference.
      setStage("safe-connect");
      return;
    }
    try {
      await submitSenderInfo({
        txId,
        body: {
          senderFullName: values.senderFullName,
          senderCountryIso: values.senderCountryIso,
          selfOrThirdParty: values.selfOrThirdParty,
          senderStreetAddress: values.senderStreetAddress || undefined,
          senderTownName: values.senderTownName || undefined,
          senderPostCode: values.senderPostCode || undefined,
        },
      }).unwrap();
      setStage("submitted");
      onComplete?.();
    } catch (err) {
      // RTK Query surfaces the error via `submitError`; the form below renders it.
    }
  };

  if (stage === "submitted") {
    return (
      <Alert severity="success" data-testid="deposit-action-required-success">
        <Typography fontWeight={600}>Sender information submitted</Typography>
        <Typography variant="body2">
          We've forwarded the details to compliance. Your deposit will be released once cleared.
        </Typography>
      </Alert>
    );
  }

  if (stage === "safe-connect" && submittedValues) {
    return (
      <Stack spacing={2} data-testid="deposit-action-required-safe-connect">
        <Alert severity="info">
          <Typography variant="body2">
            Verify wallet ownership with the widget below. We'll update your deposit once it
            completes.
          </Typography>
        </Alert>
        <NotabeneHostedMount
          kind="deposit-assist"
          txId={txId}
          asset={asset}
          sourceAddress={sourceAddress}
          amount={amount}
          // The name the customer just gave us — feeds the widget's "Myself"
          // path so it never emits an empty IVMS person.
          customerName={submittedValues.senderFullName}
          counterpartyAssist
          onComplete={async (result) => {
            try {
              await submitSenderInfo({
                txId,
                body: {
                  senderFullName: submittedValues.senderFullName,
                  senderCountryIso: submittedValues.senderCountryIso,
                  selfOrThirdParty: "self",
                  walletVerificationReference: result.walletVerificationReference,
                  ivms101: result.ivms101,
                  confirmRelationship: result.confirmRelationship,
                },
              }).unwrap();
              setStage("submitted");
              onComplete?.();
            } catch {
              // Stay on the safe-connect stage and let `submitError` (rendered
              // below) tell the customer the submission failed. Do NOT advance
              // to "submitted" — that would falsely show the green success
              // Alert when the backend's completeTransfer call actually failed
              // and Notabene was never told about the deposit. Same shape as
              // the third-party branch's handleSubmit above.
            }
          }}
          onCancel={() => setStage("form")}
          redirectFallbackUrl={`${window.location.origin}/transfers/${txId}?notabene_callback=1`}
        />
        {submitError && (
          <Alert
            severity="error"
            data-testid="deposit-action-required-safe-connect-submit-error">
            Wallet verification completed but submitting your deposit details
            failed. Please return to the form and try again, or contact support
            if the problem persists.
          </Alert>
        )}
        <Box>
          <Button
            variant="text"
            onClick={() => setStage("form")}
            data-testid="deposit-action-required-back">
            Back to form
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Formik<FormValues>
      initialValues={{
        senderFullName: "",
        senderCountryIso: "",
        selfOrThirdParty: "self",
        senderStreetAddress: "",
        senderTownName: "",
        senderPostCode: "",
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({ values, setFieldValue, isSubmitting: formSubmitting, isValid }) => (
        <Form data-testid="deposit-action-required-form">
          <Stack spacing={2.5}>
            <FormikText
              name="senderFullName"
              label="Sender full name"
              placeholder="Full legal name"
              fullWidth
              required
              data-testid="deposit-action-required-sender-name"
            />
            <Autocomplete
              options={countries}
              getOptionLabel={(o) => o.name}
              value={countries.find((c) => c.iso === values.senderCountryIso) ?? null}
              onChange={(_, value) =>
                setFieldValue("senderCountryIso", value?.iso ?? "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="senderCountryIso"
                  label="Sender country"
                  required
                  data-testid="deposit-action-required-sender-country"
                />
              )}
            />
            <FormControl>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Who sent these funds?
              </Typography>
              <RadioGroup
                row
                name="selfOrThirdParty"
                value={values.selfOrThirdParty}
                onChange={(e) => setFieldValue("selfOrThirdParty", e.target.value)}>
                <FormControlLabel
                  value="self"
                  control={<Radio data-testid="deposit-action-required-self" />}
                  label="Me (own wallet)"
                />
                <FormControlLabel
                  value="third_party"
                  control={<Radio data-testid="deposit-action-required-third-party" />}
                  label="Someone else"
                />
              </RadioGroup>
            </FormControl>
            {values.selfOrThirdParty === "third_party" && (
              <Stack spacing={2} data-testid="deposit-action-required-third-party-address">
                <Typography variant="body2" color="text.secondary">
                  Sender's address — required for compliance when the funds came
                  from someone else.
                </Typography>
                <FormikText
                  name="senderStreetAddress"
                  label="Sender street address"
                  fullWidth
                  required
                  data-testid="deposit-action-required-sender-street"
                />
                <Box display="flex" gap={2}>
                  <FormikText
                    name="senderTownName"
                    label="Town / city"
                    fullWidth
                    required
                    data-testid="deposit-action-required-sender-town"
                  />
                  <FormikText
                    name="senderPostCode"
                    label="Postcode"
                    fullWidth
                    required
                    data-testid="deposit-action-required-sender-postcode"
                  />
                </Box>
              </Stack>
            )}
            {submitError && (
              <Alert severity="error" data-testid="deposit-action-required-submit-error">
                Submission failed. Please try again or contact support.
              </Alert>
            )}
            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                disabled={!isValid || isSubmitting || formSubmitting}
                data-testid="deposit-action-required-submit">
                {isSubmitting || formSubmitting ? (
                  <CircularProgress size={20} sx={{ color: "inherit" }} />
                ) : values.selfOrThirdParty === "self" ? (
                  "Verify wallet"
                ) : (
                  "Submit"
                )}
              </Button>
            </Box>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default DepositActionRequiredForm;
